import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { useCRMAuth } from '@/contexts/CRMAuthContext';
import CRMLayout from './CRMLayout';
import { toast } from 'sonner';
import {
  Building2, Globe, Mail, Phone, User, Calendar, ArrowLeft,
  Loader2, CheckCircle2, Clock, FileText, TrendingUp, Target,
  Key, Copy, ExternalLink
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function CRMClientDetail() {
  const { clientId } = useParams();
  const { token, canViewPricing, isAdmin } = useCRMAuth();
  const [client, setClient] = useState(null);
  const [stats, setStats] = useState(null);
  const [recentTasks, setRecentTasks] = useState([]);
  const [recentLogs, setRecentLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [portalCode, setPortalCode] = useState(null);
  const [generatingCode, setGeneratingCode] = useState(false);

  useEffect(() => {
    fetchClientData();
  }, [clientId]);

  const fetchClientData = async () => {
    try {
      const [clientRes, statsRes, tasksRes, logsRes] = await Promise.all([
        fetch(`${API}/api/crm/clients/${clientId}`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API}/api/crm/clients/${clientId}/stats`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API}/api/crm/tasks/?client_id=${clientId}`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API}/api/crm/tasks/logs/?client_id=${clientId}`, { headers: { 'Authorization': `Bearer ${token}` } })
      ]);

      if (clientRes.ok) setClient((await clientRes.json()).client);
      if (statsRes.ok) setStats((await statsRes.json()).stats);
      if (tasksRes.ok) setRecentTasks((await tasksRes.json()).tasks.slice(0, 5));
      if (logsRes.ok) setRecentLogs((await logsRes.json()).logs.slice(0, 5));
    } catch (err) {
      console.error('Failed to fetch client data:', err);
      toast.error('Failed to load client data');
    } finally {
      setLoading(false);
    }
  };

  const generatePortalCode = async () => {
    setGeneratingCode(true);
    try {
      const res = await fetch(`${API}/api/crm/portal/admin/generate-code/${clientId}?token=${token}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setPortalCode(data.access_code);
        toast.success('Portal access code generated!');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to generate code');
      }
    } catch (err) {
      toast.error('Failed to generate code');
    } finally {
      setGeneratingCode(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  if (loading) {
    return (
      <CRMLayout>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      </CRMLayout>
    );
  }

  if (!client) {
    return (
      <CRMLayout>
        <div className="text-center py-20">
          <h2 className="text-xl font-semibold text-slate-900">Client not found</h2>
          <Link to="/crm/clients"><Button className="mt-4">Back to Clients</Button></Link>
        </div>
      </CRMLayout>
    );
  }

  return (
    <CRMLayout>
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Link to="/crm/clients">
          <Button variant="ghost" size="icon"><ArrowLeft className="w-5 h-5" /></Button>
        </Link>
        <div className="flex items-center gap-4 flex-1">
          <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center text-white text-2xl font-bold">
            {client.client_name?.charAt(0)}
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">{client.client_name}</h1>
            <p className="text-slate-500">{client.company_name || client.industry}</p>
          </div>
        </div>
        <Badge variant={client.status === 'active' ? 'default' : 'secondary'} className="text-sm px-3 py-1">
          {client.status}
        </Badge>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card className="p-4 bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <div className="flex items-center gap-3">
            <Target className="w-8 h-8 opacity-80" />
            <div>
              <p className="text-sm opacity-90">Total Tasks</p>
              <p className="text-2xl font-bold">{stats?.total_tasks || 0}</p>
            </div>
          </div>
        </Card>
        <Card className="p-4 bg-gradient-to-br from-emerald-500 to-emerald-600 text-white">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="w-8 h-8 opacity-80" />
            <div>
              <p className="text-sm opacity-90">Completed</p>
              <p className="text-2xl font-bold">{stats?.completed_tasks || 0}</p>
            </div>
          </div>
        </Card>
        <Card className="p-4 bg-gradient-to-br from-amber-500 to-amber-600 text-white">
          <div className="flex items-center gap-3">
            <FileText className="w-8 h-8 opacity-80" />
            <div>
              <p className="text-sm opacity-90">Work Logs</p>
              <p className="text-2xl font-bold">{stats?.total_logs || 0}</p>
            </div>
          </div>
        </Card>
        {canViewPricing() && (
          <Card className="p-4 bg-gradient-to-br from-purple-500 to-purple-600 text-white">
            <div className="flex items-center gap-3">
              <TrendingUp className="w-8 h-8 opacity-80" />
              <div>
                <p className="text-sm opacity-90">Total Value</p>
                <p className="text-2xl font-bold">₹{(stats?.total_value || 0).toLocaleString()}</p>
              </div>
            </div>
          </Card>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Client Info */}
        <Card className="p-5">
          <h3 className="font-semibold text-slate-900 mb-4">Client Information</h3>
          <div className="space-y-3">
            {client.website && (
              <div className="flex items-center gap-3 text-sm">
                <Globe className="w-4 h-4 text-slate-400" />
                <a href={client.website} target="_blank" rel="noreferrer" className="text-indigo-600 hover:underline">{client.website}</a>
              </div>
            )}
            {client.email && (
              <div className="flex items-center gap-3 text-sm">
                <Mail className="w-4 h-4 text-slate-400" />
                <span>{client.email}</span>
              </div>
            )}
            {client.phone && (
              <div className="flex items-center gap-3 text-sm">
                <Phone className="w-4 h-4 text-slate-400" />
                <span>{client.phone}</span>
              </div>
            )}
            {client.contact_person && (
              <div className="flex items-center gap-3 text-sm">
                <User className="w-4 h-4 text-slate-400" />
                <span>{client.contact_person}</span>
              </div>
            )}
            {client.industry && (
              <div className="flex items-center gap-3 text-sm">
                <Building2 className="w-4 h-4 text-slate-400" />
                <span>{client.industry}</span>
              </div>
            )}
            {canViewPricing() && client.monthly_retainer > 0 && (
              <div className="pt-3 border-t mt-3">
                <p className="text-sm text-slate-500">Monthly Retainer</p>
                <p className="text-lg font-semibold text-emerald-600">₹{client.monthly_retainer?.toLocaleString()}</p>
              </div>
            )}
          </div>
        </Card>

        {/* Portal Access (Admin Only) */}
        {isAdmin() && (
          <Card className="p-5 lg:col-span-3" data-testid="portal-access-card">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                  <Key className="w-4 h-4" />
                  Client Portal Access
                </h3>
                <p className="text-sm text-slate-500 mt-1">
                  Generate an access code for the client to view their dashboard
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                {portalCode ? (
                  <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-2">
                    <span className="font-mono text-lg font-bold text-emerald-700 tracking-widest">{portalCode}</span>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => copyToClipboard(portalCode)}
                      className="text-emerald-600 hover:text-emerald-700"
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <Button 
                    onClick={generatePortalCode} 
                    disabled={generatingCode || !client.email}
                    className="bg-indigo-600 hover:bg-indigo-700"
                    data-testid="generate-portal-code-btn"
                  >
                    {generatingCode ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
                    ) : (
                      <><Key className="w-4 h-4 mr-2" /> Generate Access Code</>
                    )}
                  </Button>
                )}
                
                <a 
                  href="/portal/login" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-sm text-indigo-600 hover:underline"
                >
                  <ExternalLink className="w-4 h-4" />
                  Portal URL
                </a>
              </div>
            </div>
            
            {!client.email && (
              <p className="text-sm text-amber-600 mt-3 flex items-center gap-1">
                <Clock className="w-4 h-4" />
                Add client email to enable portal access
              </p>
            )}
            
            {portalCode && (
              <div className="mt-4 p-3 bg-slate-50 rounded-lg text-sm text-slate-600">
                <p><strong>Share with client:</strong></p>
                <p className="mt-1">Email: <span className="font-medium">{client.email}</span></p>
                <p>Access Code: <span className="font-mono font-bold">{portalCode}</span></p>
                <p className="mt-1">Portal URL: <span className="text-indigo-600">{window.location.origin}/portal/login</span></p>
              </div>
            )}
          </Card>
        )}

        {/* Recent Tasks */}
        <Card className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-slate-900">Recent Tasks</h3>
            <Link to={`/crm/tasks?client=${clientId}`} className="text-sm text-indigo-600 hover:underline">View all</Link>
          </div>
          {recentTasks.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-4">No tasks yet</p>
          ) : (
            <div className="space-y-3">
              {recentTasks.map(task => (
                <div key={task.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50">
                  <div className={`w-2 h-2 rounded-full ${task.status === 'completed' ? 'bg-emerald-500' : task.status === 'in_progress' ? 'bg-blue-500' : 'bg-amber-500'}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">{task.title}</p>
                    <p className="text-xs text-slate-500">{task.assigned_to_name}</p>
                  </div>
                  <Badge variant="outline" className="text-xs">{task.status}</Badge>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Recent Work Logs */}
        <Card className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-slate-900">Recent Work</h3>
            <Link to={`/crm/work-logs?client=${clientId}`} className="text-sm text-indigo-600 hover:underline">View all</Link>
          </div>
          {recentLogs.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-4">No work logged yet</p>
          ) : (
            <div className="space-y-3">
              {recentLogs.map(log => (
                <div key={log.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50">
                  <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center">
                    <FileText className="w-4 h-4 text-indigo-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">{log.work_type_name}</p>
                    <p className="text-xs text-slate-500">{new Date(log.work_date).toLocaleDateString()}</p>
                  </div>
                  <Badge className="bg-blue-100 text-blue-800">{log.quantity}</Badge>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Work Package Progress */}
      {stats?.work_package && stats.work_package.length > 0 && (
        <Card className="p-5 mt-6">
          <h3 className="font-semibold text-slate-900 mb-4">Monthly Work Package Progress</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {stats.work_package.map((item, idx) => (
              <div key={idx} className="p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-slate-900">{item.work_type_name}</span>
                  <span className="text-sm text-slate-500">{item.completed}/{item.target}</span>
                </div>
                <Progress value={(item.completed / item.target) * 100} className="h-2" />
              </div>
            ))}
          </div>
        </Card>
      )}
    </CRMLayout>
  );
}
